
import { firestore, auth } from './firebase';
import { SavedProject, MasterTemplate } from './types';
import { getServerExportClient } from './server-export-client';
import { fixSubtitleOverlaps } from './utils/subtitle-helpers';

const COLLECTION_NAME = 'projects';

const hashString = (value: string): string => {
    let hash = 5381;
    for (let i = 0; i < value.length; i++) {
        hash = ((hash << 5) + hash) ^ value.charCodeAt(i);
    }
    return (hash >>> 0).toString(36);
};

const stripUndefinedDeep = (value: any): any => {
    if (Array.isArray(value)) {
        return value
            .map((entry) => stripUndefinedDeep(entry))
            .filter((entry) => entry !== undefined);
    }

    if (value && typeof value === 'object') {
        const cleanedEntries = Object.entries(value)
            .map(([key, entry]) => [key, stripUndefinedDeep(entry)] as const)
            .filter(([, entry]) => entry !== undefined);
        return Object.fromEntries(cleanedEntries);
    }

    return value === undefined ? undefined : value;
};

// Helper to sanitize clips (remove blob URLs that can't be persisted)
const sanitizeClips = (clips: any[]): any[] => {
    return clips.map(clip => {
        const sanitized = { ...clip };

        // 1. Remove Blob URLs
        if (sanitized.url && sanitized.url.startsWith('blob:')) {
            console.warn(`[Firestore] Sanitzing blob URL for clip ${clip.name}: ${clip.url}`);
            // sanitized.url = `file-requirement:${clip.name}`; // NO! This corrupts the data.
            sanitized.url = ''; // Clear it so it forces a relink/upload, rather than a protocol error
        }

        // Remove session-only client fields before persistence/share.
        if (sanitized.localUrl) {
            delete sanitized.localUrl;
        }
        if (sanitized._file) {
            delete sanitized._file;
        }
        if (sanitized._cachedBlob) {
            delete sanitized._cachedBlob;
        }

        // 2. PRUNE: Remove heavy assets to keep document under 1MB limit
        // These will be regenerated on-the-fly when the project is loaded.
        if (sanitized.thumbnails) {
            delete sanitized.thumbnails;
        }
        if (sanitized.waveform) {
            delete sanitized.waveform;
        }
        // SUBTITLE PERSISTENCE STRATEGY:
        // 1. Upload only when subtitle content changed (via subtitlesHash)
        // 2. Keep a compact inline copy (_compactSubtitles) as fallback for small subtitle sets
        // 3. On load, prefer R2 (subtitlesUrl) but fallback to _compactSubtitles
        if (sanitized.subtitles) {
            const subtitleJson = JSON.stringify(sanitized.subtitles);
            const subtitleSizeKB = subtitleJson.length / 1024;
            const newSubtitleHash = hashString(subtitleJson);
            const hadHash = typeof sanitized.subtitlesHash === 'string';
            const hasChanged = !hadHash || sanitized.subtitlesHash !== newSubtitleHash;

            if (hasChanged || !sanitized.subtitlesUrl) {
                sanitized._pendingSubtitles = sanitized.subtitles;
                delete sanitized.subtitlesUrl; // Force upload only when changed/missing
            }

            sanitized.subtitlesHash = newSubtitleHash;

            // Keep inline fallback if small enough (< 200KB per clip)
            // This prevents subtitle loss when R2 upload fails
            if (subtitleSizeKB < 200) {
                sanitized._compactSubtitles = sanitized.subtitles;
            } else {
                console.log(`[Firestore] Subtitles for ${clip.name} too large for inline (${subtitleSizeKB.toFixed(1)}KB), R2 only`);
                delete sanitized._compactSubtitles;
            }

            // Always remove the main subtitles field to keep doc smaller
            delete sanitized.subtitles;
        }

        // Recursively sanitize syntheData
        if (sanitized.syntheData && sanitized.syntheData.clips) {
            sanitized.syntheData.clips = sanitizeClips(sanitized.syntheData.clips);
        }

        return sanitized;
    });
};

// Throttle map for auto-share writes (max once per 30s per project)
const _autoShareTimestamps = new Map<string, number>();

// Promise queue to prevent "Write stream exhausted" during mass updates
const _projectSaveQueue = new Map<string, Promise<void>>();

export const saveProjectToFirestore = async (project: SavedProject): Promise<void> => {
    const executeSave = async () => {
        const user = auth.currentUser;
        if (!user) throw new Error('User not authenticated');

        // DEFENSIVE GUARD: Prevent saving "SidebarProject" objects (which lack montages but have 'updated' string)
        // This stops the data corruption bug where lightweight sidebar objects overwrite full projects.
        if ((project as any).updated && typeof (project as any).updated === 'string') {
            console.error('[Firestore] CRITICAL: Attempted to save a SidebarProject as a SavedProject! Aborting save to prevent data loss.', project);
            // We throw to alert the caller, but we DO NOT save.
            throw new Error('CORRUPTION PROTECTION: Cannot save SidebarProject to Firestore.');
        }

        // Ensure we don't accidentally wipe montageVersions if they are somehow missing in the input object
        // (unless it's a truly new project, but even then, it should be an empty array)
        if (!project.montageVersions && (project as any).clips?.length > 0) {
            console.warn('[Firestore] Warning: Saving project with NO montageVersions field. This might be data loss.', project);
        }

        const projectRef = firestore.collection('users').doc(user.uid).collection(COLLECTION_NAME).doc(project.id);

        // Deep clone first
        const projectData = JSON.parse(JSON.stringify(project));

        // SANITIZE: Remove blob URLs from clips to prevent "net::ERR_FILE_NOT_FOUND" on reload
        // We expect R2 uploads to replace these before save, but if a save happens MID-upload, 
        // we must not save the blob URL.
        if (projectData.clips) {
            projectData.clips = sanitizeClips(projectData.clips);
        }

        // Also sanitize montageVersions
        if (projectData.montageVersions) {
            // Log to confirm we are saving montages
            console.log(`[Firestore] Sanitizing ${projectData.montageVersions.length} montage versions for save.`);
            projectData.montageVersions = projectData.montageVersions.map((v: any) => ({
                ...v,
                clips: sanitizeClips(v.clips || [])
            }));
        }

        // Sanitize top-level project thumbnail
        if (projectData.thumbnailUrl && projectData.thumbnailUrl.startsWith('blob:')) {
            console.warn(`[Firestore] Sanitizing top-level blob thumbnailUrl for project ${project.id}`);
            projectData.thumbnailUrl = ''; // Clear stale blob URL
        }

        if (Array.isArray(projectData.messages)) {
            projectData.messages = projectData.messages
                .filter((message: any) => !message?.isLoading && !message?.pendingChoice)
                .map((message: any) => {
                    const sanitizedMessage = { ...message };
                    delete sanitizedMessage.onCancel;
                    delete sanitizedMessage.action;
                    return sanitizedMessage;
                });
        }

        // =========================================================================
        // ASYNC R2 SUBTITLE UPLOAD
        // Upload pending subtitles to R2, store URL reference in clip
        // =========================================================================
        const exportClient = getServerExportClient(user.uid);
        const allClips = [
            ...(projectData.clips || []),
            ...(projectData.montageVersions?.flatMap((v: any) => v.clips || []) || [])
        ];
        const uploadedSubtitleKeys = new Set<string>();

        const uploadPromises = allClips
            .filter((clip: any) => clip._pendingSubtitles)
            .filter((clip: any) => {
                const subtitleKey = `${clip.id}:${clip.subtitlesHash || ''}`;
                if (uploadedSubtitleKeys.has(subtitleKey)) return false;
                uploadedSubtitleKeys.add(subtitleKey);
                return true;
            })
            .map(async (clip: any) => {
                try {
                    const url = await exportClient.saveSubtitles(
                        project.id,
                        clip.id,
                        clip._pendingSubtitles
                    );
                    if (url) {
                        clip.subtitlesUrl = url;
                        delete clip._pendingSubtitles; // Only clean up on success
                        console.log(`[Firestore] Subtitles uploaded to R2 for clip ${clip.name}: ${url}`);
                    } else {
                        console.warn(`[Firestore] R2 returned no URL for clip ${clip.name}, keeping _compactSubtitles fallback`);
                        if (!clip._compactSubtitles) {
                            throw new Error(`Failed to upload subtitles for ${clip.name} and no fallback available.`);
                        }
                        delete clip._pendingSubtitles; // Clear it to prevent infinite retries if the server is just returning null
                    }
                } catch (err) {
                    console.error(`[Firestore] Failed to upload subtitles for clip ${clip.name}:`, err);
                    if (!clip._compactSubtitles) {
                        // CRITICAL FIX: If we have no fallback, we MUST NOT save the project.
                        // Throwing here will abort the `Promise.all` and the entire `saveProjectToFirestore` call.
                        // The frontend will keep `_pendingSubtitles` in memory and try again on the next auto-save.
                        throw new Error(`Failed to save project: Subtitle upload failed for ${clip.name} and it is too large for inline fallback.`);
                    } else {
                        // If we have a fallback, we can tolerate the R2 failure for now and clean up
                        delete clip._pendingSubtitles;
                    }
                }
            });

        // Wait for all subtitle uploads (non-blocking for save, but we wait)
        if (uploadPromises.length > 0) {
            console.log(`[Firestore] Uploading ${uploadPromises.length} subtitle sets to R2...`);
            await Promise.all(uploadPromises);
        }

        // Report estimated size for debugging
        // Final safety measure: Ensure it's pure JSON to avoid Firestore 400 errors (undefined, weird objects)
        const finalPayload = JSON.parse(JSON.stringify(projectData));
        const jsonString = JSON.stringify(finalPayload);
        const sizeInKBytes = (jsonString.length / 1024).toFixed(1);

        console.log(`[Firestore] Saving project ${project.id}. Estimated size: ${sizeInKBytes} KB. Montages: ${finalPayload.montageVersions?.length || 0}`);

        try {
            await projectRef.set({
                ...finalPayload,
                updatedAt: Date.now() // Force update timestamp
            }, { merge: true });

            // Auto-share: mirror to sharedProjects (throttled to avoid write exhaustion)
            const now = Date.now();
            const lastShare = _autoShareTimestamps.get(project.id) || 0;
            if (now - lastShare > 30000) { // Max once per 30s per project
                _autoShareTimestamps.set(project.id, now);
                firestore.collection('sharedProjects').doc(project.id).set({
                    ...finalPayload,
                    ownerId: user.uid,
                    ownerEmail: user.email,
                    updatedAt: now,
                    sharedAt: now,
                }, { merge: true }).catch(err => {
                    console.warn(`[Firestore] Auto-share failed for project ${project.id} (non-blocking):`, err);
                });
            }
        } catch (error) {
            console.error(`[Firestore] FATAL SAVE ERROR for project ${project.id}:`, error);
            throw error; // Re-throw so caller knows it failed
        }
    };

    // Serialize saves per project to avoid Firestore "Write stream exhausted"
    const currentQueue = _projectSaveQueue.get(project.id) || Promise.resolve();
    const nextQueue = currentQueue.then(() => executeSave()).catch(err => {
        console.error('[Firestore] Queue save error:', err);
    });

    _projectSaveQueue.set(project.id, nextQueue);
    return nextQueue;
};

export const getProjectsFromFirestore = async (): Promise<SavedProject[]> => {
    const user = auth.currentUser;
    if (!user) return [];

    const snapshot = await firestore.collection('users').doc(user.uid).collection(COLLECTION_NAME).orderBy('updatedAt', 'desc').get();

    console.log(`[Firestore] Loaded ${snapshot.size} projects from Firestore`);

    // Normalize and clean projects
    const projects = snapshot.docs.map(doc => {
        const data = doc.data() as any;

        // NORMALIZE: Fix corrupted projects that have SidebarProject fields
        // These were saved incorrectly and have 'title'/'updated' instead of 'name'/'updatedAt'
        const normalized: SavedProject = {
            id: doc.id,
            name: data.name || data.title || 'Untitled', // Prefer 'name', fallback to 'title'
            thumbnailUrl: data.thumbnailUrl || '',
            clips: data.clips || [],
            tracks: data.tracks || [],
            montageVersions: data.montageVersions || [],
            exports: data.exports || [],
            aiSuggestions: data.aiSuggestions,
            messages: data.messages,
            createdAt: data.createdAt || Date.now(),
            updatedAt: data.updatedAt || (data.updated ? new Date(data.updated).getTime() : Date.now()),
            isShared: data.isShared
        };

        // Log normalization if it happened
        if (data.title && !data.name) {
            console.warn(`[Firestore] Normalized corrupted project ${doc.id}: 'title' -> 'name'`);
        }
        if (data.updated && !data.updatedAt) {
            console.warn(`[Firestore] Normalized corrupted project ${doc.id}: 'updated' -> 'updatedAt'`);
        }

        if (normalized.montageVersions?.length) {
            console.log(`[Firestore] Project ${doc.id} has ${normalized.montageVersions.length} montages:`, normalized.montageVersions.map((m: any) => m.name));
        } else {
            console.log(`[Firestore] Project ${doc.id} has NO montages.`);
        }

        return normalized;
    });

    return projects;
};

/**
 * Load subtitles from R2 for clips that have a subtitlesUrl.
 * Call this when opening a project to hydrate subtitle data.
 */
export const loadSubtitlesForProject = async (project: SavedProject): Promise<SavedProject> => {
    const user = auth.currentUser;
    if (!user) return project;

    const exportClient = getServerExportClient(user.uid);
    const normalizeSubtitleMap = (subtitleMap: any) => {
        if (!subtitleMap || typeof subtitleMap !== 'object') return subtitleMap;
        const normalized: any = {};
        for (const [lang, subs] of Object.entries(subtitleMap)) {
            normalized[lang] = Array.isArray(subs) ? fixSubtitleOverlaps(subs as any) : subs;
        }
        return normalized;
    };

    // Collect all clips that need subtitle loading
    const clipsToHydrate: Array<{ clip: any; projectId: string }> = [];

    const checkClip = (clip: any) => {
        if (clip.subtitles) return; // Already has subtitles in memory

        if ((clip as any).subtitlesUrl) {
            // Has R2 URL — fetch from R2
            clipsToHydrate.push({ clip, projectId: project.id });
        } else if ((clip as any)._compactSubtitles) {
            // Has inline fallback — use it directly
            clip.subtitles = normalizeSubtitleMap((clip as any)._compactSubtitles);
            console.log(`[Firestore] Restored subtitles from inline fallback for clip ${clip.name}`);
        }
    };

    for (const clip of project.clips || []) {
        checkClip(clip);
    }

    for (const version of project.montageVersions || []) {
        for (const clip of version.clips || []) {
            checkClip(clip);
        }
    }

    if (clipsToHydrate.length === 0) {
        return project;
    }

    console.log(`[Firestore] Loading subtitles from R2 for ${clipsToHydrate.length} clips...`);

    await Promise.all(clipsToHydrate.map(async ({ clip, projectId }) => {
        try {
            const subtitles = await exportClient.loadSubtitles(projectId, clip.id);
            if (subtitles) {
                clip.subtitles = normalizeSubtitleMap(subtitles);
                console.log(`[Firestore] Loaded subtitles for clip ${clip.name}`);
            } else if ((clip as any)._compactSubtitles) {
                // R2 returned null (404) — use inline fallback
                clip.subtitles = normalizeSubtitleMap((clip as any)._compactSubtitles);
                console.log(`[Firestore] R2 empty, using inline fallback for clip ${clip.name}`);
            }
        } catch (err) {
            console.error(`[Firestore] Failed to load subtitles for clip ${clip.name}:`, err);
            // Fallback to inline compact subtitles if R2 fails
            if ((clip as any)._compactSubtitles) {
                clip.subtitles = normalizeSubtitleMap((clip as any)._compactSubtitles);
                console.log(`[Firestore] R2 failed, using inline fallback for clip ${clip.name}`);
            }
        }
    }));

    return project;
};

export const deleteProjectFromFirestore = async (projectId: string): Promise<void> => {
    const user = auth.currentUser;
    if (!user) throw new Error('User not authenticated');

    console.log(`[Firestore] Deleting project ${projectId}...`);
    await firestore.collection('users').doc(user.uid).collection(COLLECTION_NAME).doc(projectId).delete();
    console.log(`[Firestore] Project ${projectId} deleted successfully.`);
};
export const renameProjectInFirestore = async (projectId: string, newName: string): Promise<void> => {
    const user = auth.currentUser;
    if (!user) throw new Error('User not authenticated');

    await firestore.collection('users').doc(user.uid).collection(COLLECTION_NAME).doc(projectId).update({
        name: newName,
        title: newName, // For compatibility if 'title' was used
        updatedAt: Date.now()
    });
};

/* --- User Settings (Sidebar Persistence) --- */

export interface FolderDefinition {
    id: string;
    name: string;
    open: boolean;
    projectIds: string[]; // Only IDs are saved
    preset?: {
        key: string;
        label: string;
        icon: string;
        color: string;
    };
}

export interface UserSettings {
    folders: FolderDefinition[];
    rootProjectIds: string[]; // IDs of projects at the root level
}

const SETTINGS_COLLECTION = 'settings';
const LAYOUT_DOC = 'layout';

export const saveUserSettings = async (settings: UserSettings): Promise<void> => {
    const user = auth.currentUser;
    if (!user) {
        console.warn("[Firestore] User not authenticated, cannot save settings.");
        return;
    }

    const sanitizedFolders = settings.folders.map((f: any) => ({
        id: f.id,
        name: f.name,
        open: f.open,
        preset: f.preset || null,
        // Handle both formats: UI uses 'projects', storage uses 'projectIds'
        projectIds: f.projects ? f.projects.map((p: any) => p.id) : (f.projectIds || [])
    }));

    try {
        console.log('[Firestore] Saving user settings:', { folders: sanitizedFolders.length, root: settings.rootProjectIds.length });
        await firestore.collection('users').doc(user.uid).collection(SETTINGS_COLLECTION).doc(LAYOUT_DOC).set({
            folders: sanitizedFolders,
            rootProjectIds: settings.rootProjectIds,
            updatedAt: Date.now()
        }, { merge: true });
        console.log('[Firestore] User settings saved successfully');
    } catch (error) {
        console.error("[Firestore] Failed to save user settings:", error);
    }
};

export const loadUserSettings = async (): Promise<UserSettings | null> => {
    const user = auth.currentUser;
    if (!user) {
        console.log('[Firestore] Cannot load settings: No user');
        return null;
    }

    try {
        const doc = await firestore.collection('users').doc(user.uid).collection(SETTINGS_COLLECTION).doc(LAYOUT_DOC).get();
        if (!doc.exists) {
            console.warn('[Firestore] No user settings doc found');
            return null;
        }

        const data = doc.data() as any;
        console.log('[Firestore] Loaded user settings:', { folders: data.folders?.length, root: data.rootProjectIds?.length });
        return {
            folders: data.folders || [],
            rootProjectIds: data.rootProjectIds || []
        };
    } catch (error) {
        console.error("[Firestore] Failed to load user settings:", error);
        return null;
    }
};

/**
 * Fetch a project by ID. Used for shareable project URLs.
 * First checks the current user's projects, then falls back to shared projects.
 */
export const getProjectById = async (projectId: string): Promise<SavedProject | null> => {
    const user = auth.currentUser;
    if (!user) return null;

    try {
        // 1. First check user's own projects
        const userDoc = await firestore.collection('users').doc(user.uid).collection(COLLECTION_NAME).doc(projectId).get();
        if (userDoc.exists) {
            return userDoc.data() as SavedProject;
        }

        // 2. Fallback to shared projects collection
        const sharedDoc = await firestore.collection('sharedProjects').doc(projectId).get();
        if (sharedDoc.exists) {
            const data = sharedDoc.data() as SavedProject & { ownerId?: string; ownerEmail?: string };
            return {
                ...data,
                isShared: true, // Mark as shared for UI differentiation
            } as SavedProject;
        }

        return null;
    } catch (error) {
        console.error("[Firestore] Failed to get project by ID:", error);
        return null;
    }
};

/**
 * Share a project publicly. Copies the project to `sharedProjects` collection
 * where any authenticated user can read it.
 * @returns The shareable URL
 */
export const shareProject = async (project: SavedProject): Promise<string> => {
    const user = auth.currentUser;
    if (!user) throw new Error('User not authenticated');

    const sharedRef = firestore.collection('sharedProjects').doc(project.id);
    const userDocRef = firestore.collection('users').doc(user.uid).collection(COLLECTION_NAME).doc(project.id);

    // Deep clone and sanitize local state as fallback
    let projectDataToShare: any;

    try {
        // ALWAYS try to fetch the persisted version first.
        // The DB version has `subtitlesUrl` correctly populated and stripped of heavy inline arrays.
        // Local state often has raw `.subtitles` and NO `.subtitlesUrl` because it hasn't synced back from R2 upload yet.
        const userDoc = await userDocRef.get();
        if (userDoc.exists) {
            projectDataToShare = userDoc.data();
            console.log(`[Firestore] shareProject: Sharing persisted version of project ${project.id}`);
        }
    } catch (e) {
        console.warn(`[Firestore] shareProject: Failed to fetch persisted project ${project.id}, falling back to local state`, e);
    }

    if (!projectDataToShare) {
        // Fallback for newly created projects that haven't saved to DB yet, or if DB fetch failed.
        projectDataToShare = JSON.parse(JSON.stringify(project));
        if (projectDataToShare.clips) {
            projectDataToShare.clips = sanitizeClips(projectDataToShare.clips);
        }
        if (projectDataToShare.montageVersions) {
            projectDataToShare.montageVersions = projectDataToShare.montageVersions.map((v: any) => ({
                ...v,
                clips: sanitizeClips(v.clips || [])
            }));
        }
        console.log(`[Firestore] shareProject: Sharing local sanitized state of project ${project.id}`);
    }

    await sharedRef.set({
        ...projectDataToShare,
        ownerId: user.uid,
        ownerEmail: user.email,
        sharedAt: Date.now(),
        updatedAt: Date.now()
    });

    return `${window.location.origin}/project/${project.id}`;
};

/**
 * Unshare a project (remove from shared collection)
 */
export const unshareProject = async (projectId: string): Promise<void> => {
    const user = auth.currentUser;
    if (!user) throw new Error('User not authenticated');

    // Verify ownership before deleting
    const sharedDoc = await firestore.collection('sharedProjects').doc(projectId).get();
    if (sharedDoc.exists) {
        const data = sharedDoc.data();
        if (data?.ownerId !== user.uid) {
            throw new Error('Not authorized to unshare this project');
        }
        await firestore.collection('sharedProjects').doc(projectId).delete();
    }
};

export const saveMasterTemplate = async (template: MasterTemplate): Promise<void> => {
    const user = auth.currentUser;
    if (!user) throw new Error('User not authenticated');

    const templateRef = firestore.collection('master_templates').doc(template.id);

    // Sanitize clips in syntheData (remove blob URLs)
    const clipsToSanitize = template.syntheData.clips || [];
    // Reuse the existing sanitizeClips helper (which is not exported but available in scope)
    const sanitizedClips = sanitizeClips(clipsToSanitize);

    const sanitizedPayload = stripUndefinedDeep({
        ...template,
        syntheData: {
            ...template.syntheData,
            clips: sanitizedClips
        },
        ownerId: user.uid,
        createdAt: Date.now()
    });

    await templateRef.set(sanitizedPayload, { merge: true });

    console.log(`[Firestore] Saved Master Template ${template.id}`);
};

export const getMasterTemplates = async (): Promise<MasterTemplate[]> => {
    const user = auth.currentUser;
    if (!user) return [];

    // Fetch user's own templates (no orderBy to avoid composite index requirement)
    const snapshot = await firestore.collection('master_templates')
        .where('ownerId', '==', user.uid)
        .get();

    const templates = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as MasterTemplate));
    // Sort client-side by createdAt desc
    return templates.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
};
